import { toast } from "components/FluentToast";
import { Formik } from "formik";
import { useCreateFleetVehicleDefaultSetting } from "hooks/data/mutations/useCreateFleetVehicleDefaultSetting";
import { useUpdateFleetVehicleDefaultSetting } from "hooks/data/mutations/useUpdateFleetVehicleDefaultSetting";
import { delay } from "library/delay";
import moment from "moment";
import useActionConfig from "pages/fleet-form/hooks/useActionConfg";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { loadFleetWithInsurersActions, setDialog } from "store/fleetForm";
import { useSelector } from "store/hooks";
import {
  formatHullDeductibleOption,
  HullDeductibleOption,
  parseHullDeductibleOptions,
} from "utils/hullDeductibleOptions";
import { formatDoubleFieldValue } from "utils/utils";
import useDefaultSettingsData from "../hooks/useDefaultSettingsData";
import HullDeductibleOptionsFormFields, {
  DeductibleOptionRow,
  deductibleOptionFieldName,
  EMPTY_DEDUCTIBLE_OPTION_ROW,
} from "./HullDeductibleOptionsFormFields";

type Props = {
  settingId: string;
  attributeName: string;
  isLoading: boolean;
};

// "" -> null, "2,5" -> 2.5, "5000" -> 5000
const toNumber = (value: string, isDecimal: boolean): number | null => {
  if (value === null || value === undefined || String(value).trim() === "")
    return null;
  const n = Number(isDecimal ? formatDoubleFieldValue(value) : value);
  return Number.isNaN(n) ? null : n;
};

const toOption = (row: DeductibleOptionRow): HullDeductibleOption => ({
  percent: toNumber(row.percent, true),
  min: toNumber(row.min, false),
  max: toNumber(row.max, false),
});

// CZ: the hull deductible options of an insurer (the client picks one of them
// for the requested Hull). Only the valid from date, the insurer and the list
// of options are entered - the premium type is Hull and there are no criteria.
const HullDeductibleOptionsForm = ({
  settingId,
  attributeName,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fleet = useSelector((s) => s.fleetForm.fleetWithPremiums);
  const fleetAppSettings = useSelector((s) => s.fleetForm.fleetAppSettings);
  const taxonomy = useSelector((s) => s.taxonomy);
  const hullPremiumTypeId = taxonomy.PremiumType.byCode["PremiumType.HULL"]?.id;

  const { mutate: onUpdateFleetVehicleDefaultSetting } =
    useUpdateFleetVehicleDefaultSetting();
  const { mutate: onCreateFleetVehicleDefaultSetting } =
    useCreateFleetVehicleDefaultSetting();

  const insurerOptions = useActionConfig(fleetAppSettings).insurerOptions;

  const defaultData = useDefaultSettingsData();
  const rowVersion = defaultData?.rowVersion;

  const close = () => {
    dispatch(setDialog({ type: "", isOpen: false }));
  };

  const initialValues = useMemo(() => {
    const options: DeductibleOptionRow[] = parseHullDeductibleOptions(
      defaultData?.attributeValue,
    ).map((o) => ({
      // DoubleNumberInputField shows a decimal comma
      percent: o.percent != null ? String(o.percent).replace(".", ",") : "",
      min: o.min != null ? String(o.min) : "",
      max: o.max != null ? String(o.max) : "",
    }));
    return {
      validFromDate: defaultData?.validFromDate
        ? new Date(defaultData.validFromDate)
        : new Date(),
      insurerPartnerNumber: defaultData
        ? (insurerOptions?.find(
            (item) => item.value === defaultData.insurerPartnerNumber,
          ) ?? null)
        : null,
      options:
        options.length > 0 ? options : [{ ...EMPTY_DEDUCTIBLE_OPTION_ROW }],
    };
  }, [defaultData, insurerOptions]);

  const validate = (values) => {
    const errors = {} as Record<string, string>;
    if (!values.validFromDate) {
      errors.validFromDate = t("greco.required");
    }
    if (!values.insurerPartnerNumber?.value) {
      errors.insurerPartnerNumber = t("greco.required");
    }
    const rows: DeductibleOptionRow[] = values.options || [];
    if (rows.length === 0) {
      errors.options = t("bfm.hullDeductibleOptions.atLeastOneOption.error");
    }
    const seen = new Set<string>();
    rows.forEach((row, index) => {
      const option = toOption(row);
      const field = (f: keyof DeductibleOptionRow) =>
        deductibleOptionFieldName(index, f);
      // it is enough that one part is defined
      if (option.percent == null && option.min == null && option.max == null) {
        errors[field("percent")] = t(
          "bfm.hullDeductibleOptions.atLeastOnePart.error",
        );
        return;
      }
      if (
        option.percent != null &&
        !(option.percent > 0 && option.percent <= 100)
      ) {
        errors[field("percent")] = t("bfm.hullDeductibleOptions.percent.error");
      }
      if (option.min != null && !(option.min > 0)) {
        errors[field("min")] = t("bfm.hullDeductibleOptions.amount.error");
      }
      if (option.max != null && !(option.max > 0)) {
        errors[field("max")] = t("bfm.hullDeductibleOptions.amount.error");
      }
      if (option.min != null && option.max != null && option.min > option.max) {
        errors[field("max")] = t(
          "bfm.hullDeductibleOptions.minGreaterThanMax.error",
        );
      }
      const formatted = formatHullDeductibleOption(option);
      if (seen.has(formatted)) {
        errors[field("max")] = t(
          "bfm.hullDeductibleOptions.duplicateOption.error",
        );
      }
      seen.add(formatted);
    });
    return errors;
  };

  const onSubmit = (values) => {
    // as the other default settings: the selected day at 00:00 UTC
    const localMoment = moment(values.validFromDate);
    const utcValidFromDate = new Date(
      Date.UTC(localMoment.year(), localMoment.month(), localMoment.date()),
    );

    const body = {
      validFromDate: utcValidFromDate,
      insurerGroupPolicyNumber: null,
      attributeName,
      // undefined parts are left out of the JSON
      attributeValue: JSON.stringify(
        (values.options as DeductibleOptionRow[]).map((row) => {
          const { percent, min, max } = toOption(row);
          return {
            ...(percent != null && { percent }),
            ...(min != null && { min }),
            ...(max != null && { max }),
          };
        }),
      ),
      premiumType: hullPremiumTypeId,
      insurerPartnerNumber: values.insurerPartnerNumber?.value,
      // the options apply to all vehicles of the insurer
      settingCriteria: {},
    };

    const onSuccess = () => {
      toast.success(t("bfm.success"));
      close();
      dispatch(
        loadFleetWithInsurersActions.trigger({ fleetId: fleet.fleetId }),
      );
    };

    if (!!settingId) {
      onUpdateFleetVehicleDefaultSetting(
        {
          fleetId: fleet.fleetId,
          fleetDefaultSettingId: settingId,
          body: { fleetId: fleet.fleetId, ...body, rowVersion },
        },
        { onSuccess },
      );
    } else {
      onCreateFleetVehicleDefaultSetting(
        {
          fleetId: fleet.fleetId,
          body: { fleetId: fleet.fleetId, ...body },
        },
        { onSuccess },
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validate={validate}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting }) => {
        onSubmit(values);
        await delay(400);
        setSubmitting(false);
      }}
    >
      {(props) => (
        <div>
          <form onSubmit={props.handleSubmit}>
            <HullDeductibleOptionsFormFields
              fieldProps={props}
              insurerOptions={insurerOptions}
              isLoading={isLoading}
            />
          </form>
        </div>
      )}
    </Formik>
  );
};

export default HullDeductibleOptionsForm;
